import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import BrandHeader from '../components/BrandHeader';
import { getBrandConfig, getCurrentBrand } from '../brand-config';
import { getHolyShippedSummaries, WeekSummary } from '../data/holyShipped';
import WeekDetail from './WeekDetail';
import '../styles/holy-shipped.css';

const HolyShipped: React.FC = () => {
  const params = useParams<{ weekNumber?: string }>();
  const navigate = useNavigate();
  const [selectedWeek, setSelectedWeek] = useState<number | null>(null);
  const brandConfig = getBrandConfig();
  const currentBrand = useMemo(() => getCurrentBrand(), []);

  useEffect(() => {
    if (params.weekNumber) {
      const weekNum = parseInt(params.weekNumber);
      if (!isNaN(weekNum)) {
        setSelectedWeek(weekNum);
      }
    }
  }, [params.weekNumber]);

  const weeks = useMemo<WeekSummary[]>(
    () => getHolyShippedSummaries(currentBrand),
    [currentBrand],
  );

  if (selectedWeek !== null) {
    return (
      <WeekDetail
        weekNumber={selectedWeek}
        onBack={() => {
          setSelectedWeek(null);
          navigate('/holy-shipped');
        }}
      />
    );
  }

  if (weeks.length === 0) {
    return (
      <div
        className="app holy-shipped-app"
        style={{ '--primary-color': brandConfig.primaryColor } as React.CSSProperties}
      >
        <BrandHeader />
        <div className="dashboard">
          <main className="dashboard-main">
            <div className="dashboard-container" style={{ textAlign: 'center', padding: '4rem 0' }}>
              <h2>No Holy Shipped updates yet</h2>
              <p style={{ marginTop: '1rem' }}>
                We haven’t logged brand-specific deliverables for this time period. Check back after our next shipment.
              </p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const totalFeatures = useMemo(
    () => weeks.reduce((sum, week) => sum + week.featuresShipped, 0),
    [weeks],
  );

  const brandIcon = currentBrand === 'marsmen' ? '🚀' : currentBrand === 'hollow' ? '🧦' : '🌐';

  return (
    <div
      className="app holy-shipped-app"
      style={{ '--primary-color': brandConfig.primaryColor } as React.CSSProperties}
    >
      <BrandHeader />

      <div className="dashboard">
        {/* Stats Header */}
        <div className="hs-stats-header">
          <div className="dashboard-container">
            <div className="hs-stats-grid">
              <div className="hs-stat-card">
                <span className="hs-stat-value">{weeks.filter(w => w.status === 'complete').length}</span>
                <span className="hs-stat-label">Weeks Complete</span>
              </div>
              <div className="hs-stat-card">
                <span className="hs-stat-value">{totalFeatures}</span>
                <span className="hs-stat-label">Total Features</span>
              </div>
              <div className="hs-stat-card">
                <span className="hs-stat-value">{brandIcon}</span>
                <span className="hs-stat-label">{brandConfig.name}</span>
              </div>
            </div>
          </div>
        </div>

        <main className="dashboard-main">
          <div className="dashboard-container">
            <div className="hs-overview-header">
              <h2 className="hs-overview-title">Weekly Progress Overview</h2>
              <p className="hs-overview-subtitle">
                Track our velocity building {brandConfig.name}'s automation infrastructure and tools.
                Click any week to see detailed deliverables and technical breakdown.
              </p>
            </div>

            {/* Week Cards */}
            <div className="hs-weeks-grid">
              {weeks.map((week) => (
                <div
                  key={week.weekNumber}
                  className={`hs-week-card ${week.status}`}
                  onClick={() => week.status !== 'upcoming' && navigate(`/holy-shipped/week/${week.weekNumber}`)}
                >
                  <div className="hs-week-card-header">
                    <div className="hs-week-card-left">
                      <h3 className="hs-week-card-number">Week {week.weekNumber}</h3>
                      <p className="hs-week-card-date">{week.weekOf}</p>
                    </div>
                    <div className="hs-week-card-right">
                      <div className={`hs-week-status-badge ${week.status}`}>
                        {week.status === 'complete' ? '✅ Complete' :
                         week.status === 'in-progress' ? '🚧 In Progress' :
                         '📅 Upcoming'}
                      </div>
                    </div>
                  </div>

                  <div className="hs-week-card-content">
                    <h4 className="hs-week-card-title">{week.title}</h4>
                    <p className="hs-week-card-description">{week.description}</p>

                    <div className="hs-week-card-metrics">
                      <div className="hs-week-metric">
                        <span className="hs-week-metric-value">{week.featuresShipped}</span>
                        <span className="hs-week-metric-label">Features</span>
                      </div>
                      <div className="hs-week-metric">
                        <span className="hs-week-metric-value">{week.completionRate}%</span>
                        <span className="hs-week-metric-label">Complete</span>
                      </div>
                      {week.status === 'complete' && (
                        <div className="hs-week-metric">
                          <span className="hs-week-metric-value">📊</span>
                          <span className="hs-week-metric-label">View Details</span>
                        </div>
                      )}
                    </div>

                    <div className="hs-week-highlights">
                      <h5 className="hs-highlights-title">Key Highlights:</h5>
                      <ul className="hs-highlights-list">
                        {week.keyHighlights.map((highlight, index) => (
                          <li key={index} className="hs-highlight-item">{highlight}</li>
                        ))}
                      </ul>
                    </div>
                  </div>

                  {week.status === 'complete' && (
                    <div className="hs-week-card-footer">
                      <span className="hs-view-details-text">Click to view detailed report →</span>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </main>

        <footer className="dashboard-footer">
          <div className="footer-content">
            <p>© 2024 {brandConfig.name}. Internal automation workspace.</p>
            <div className="footer-links">
              <a href="https://automations.easystreetbrands.com" target="_blank" rel="noopener noreferrer">
                Automation Portal
              </a>
              <span className="separator">•</span>
              <a href="/" onClick={(event) => { event.preventDefault(); navigate('/'); }}>
                Brand Home
              </a>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
};

export default HolyShipped;
